/* eslint react/destructuring-assignment: 0 */
/* based on https://github.com/react-bootstrap/react-bootstrap/blob/master/src/Button.js */
import classNames from 'classnames';
import React from 'react';
import PropTypes from 'prop-types';

import Link from '../Link';
import CloseThick from '../../svg/icons/ic_close_20x20.svg';
import PreviousArrow from '../../svg/icons/ic_chevron-left_20x20.svg';
import Spinner from '../../svg/icons/processing-spinner.svg';
import AccessibleRedSpinner from '../../svg/icons/processing-spinner-accessible-red.svg';

import './Button.scss';

export const BUTTON_VARIANTS = PropTypes.oneOf([
  'primary',
  'secondary',
  'no-style',
  'back',
  'cancel',
  'close',
  'login',
  'article',
  'black',
  'black-small',
  'link',
  'link-inline',
  'double-confirm',
  'rga',
  'gda-concierge',
  'gda-primary',
  'gda-secondary',
  'sidenav',
  'gda-banner',
]);

const propTypes = {
  as: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  fullBleed: PropTypes.bool,
  fullWidth: PropTypes.bool,
  href: PropTypes.string,
  variant: BUTTON_VARIANTS,
  processing: PropTypes.bool,

  // shapes
  circle: PropTypes.bool,

  // sizes
  sm: PropTypes.bool,
  md: PropTypes.bool,
  lg: PropTypes.bool,

  // can pass in background and font colors
  colors: PropTypes.shape({
    background: PropTypes.string,
    font: PropTypes.string,
  }),

  /**
   * Defines HTML button type attribute.
   *
   * @default 'button'
   */
  type: PropTypes.oneOf(['button', 'reset', 'submit', null]),
};

const defaultProps = {
  as: 'button',
  circle: false,
  className: undefined,
  href: undefined,
  variant: 'primary',
  disabled: false,
  fullBleed: false,
  fullWidth: false,
  type: 'button',
  colors: {},
  processing: false,
  sm: false,
  md: false,
  lg: false,
};

const Button = React.forwardRef(
  (
    {
      as: Component = 'button',
      variant,
      className,
      circle,
      fullBleed,
      fullWidth,
      processing,
      sm,
      md,
      lg,
      type,
      colors,
      ...props
    },
    ref
  ) => {
    const classes = classNames(
      className,
      'Button',
      `Button--${variant}`,
      sm && 'Button--sm',
      md && 'Button--md',
      lg && 'Button--lg',
      circle && 'Button--circle',
      fullBleed && 'Button--full-bleed',
      fullWidth && 'Button--full-width',
      processing && 'Button--processing'
    );

    const style = {
      color: colors?.font ? `#${colors.font}` : null,
      background: colors?.background ? `#${colors.background}` : null,
    };

    if (props.href) {
      return (
        <Link
          {...props}
          ref={ref}
          className={classNames(classes, props.disabled && 'disabled')}
        />
      );
    }

    if (processing) {
      return (
        <Component className={classes} disabled>
          {variant === 'cancel'
            ? <AccessibleRedSpinner height="16px" width="16px" />
            : <Spinner height="16px" width="16px" />}
        </Component>
      );
    }

    if (ref) {
      props.ref = ref;
    }

    if (variant === 'close') {
      props.children = <CloseThick />;
    }

    if (variant === 'back') {
      props.children = <PreviousArrow />;
    }

    return <Component className={classes} type={type} style={style} {...props} />;
  }
);

Button.displayName = 'Button';
Button.propTypes = propTypes;
Button.defaultProps = defaultProps;

const SubmitButton = (props) => (<Button {...props} type="submit" />);
const ResetButton = (props) => (<Button {...props} type="reset" />);

export {
  ResetButton,
  SubmitButton,
};

export default Button;
